import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getMember, createMember, updateMember } from '../../api/fpaMembers';
import { listEstates } from '../../api/estates';
import {
  Container,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import TagSelector from '../../components/fpa/TagSelector';
import ContactList from '../../components/fpa/ContactList';
import ParcelPicker from '../../components/fpa/ParcelPicker';
import MobileFormFooter from '../../components/common/MobileFormFooter';
import { parseApiError } from '@/lib/apiErrors';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';

// Predefined tags
const PREDEFINED_HAZARDS = [
  'thatch', 'chemicals', 'gas_cylinders', 'fuel_storage', 'timber',
  'hay_bales', 'paint', 'fertilizer', 'explosives', 'electrical_equipment',
];

const PREDEFINED_ANIMALS = [
  'cattle', 'horses', 'sheep', 'goats', 'pigs',
  'poultry', 'dogs', 'cats', 'wildlife', 'exotic',
];

// Validation schema
const validationSchema = Yup.object({
  ref_number: Yup.string().required('Reference number is required'),
  name: Yup.string().required('Name is required'),
  plot_number: Yup.string().nullable(),
  street: Yup.string().nullable(),
  area_suburb: Yup.string().nullable(),
  size_hectares: Yup.number().nullable().positive('Must be a positive number'),
  estate_id: Yup.number().nullable(),
  member_since: Yup.string().nullable(),
});

const FPAMemberForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { tenantId } = useTenant();
  const { currentUser } = useAuth();
  const isEditMode = Boolean(id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estates, setEstates] = useState([]);
  const [selectedEstate, setSelectedEstate] = useState(null);
  const [hazards, setHazards] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [parcelId, setParcelId] = useState(null);
  const [notes, setNotes] = useState('');

  const formik = useFormik({
    initialValues: {
      ref_number: '',
      name: '',
      plot_number: '',
      street: '',
      area_suburb: '',
      size_hectares: '',
      estate_id: null,
      member_since: '',
      member_type: 'member',
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  // Fetch estates for autocomplete
  useEffect(() => {
    const fetchEstates = async () => {
      try {
        const data = await listEstates({ page: 1, page_size: 100 });
        setEstates(data.estates || []);
      } catch (error) {
        console.error('Error fetching estates:', error);
      }
    };
    fetchEstates();
  }, []);

  // Fetch member data for edit mode
  useEffect(() => {
    if (isEditMode) {
      setIsLoading(true);
      getMember(id)
        .then((member) => {
          formik.setValues({
            ref_number: member.ref_number || '',
            name: member.name || '',
            plot_number: member.plot_number || '',
            street: member.street || '',
            area_suburb: member.area_suburb || '',
            size_hectares: member.size_hectares || '',
            estate_id: member.estate_id || null,
            member_since: member.member_since ? member.member_since.substring(0, 10) : '',
            member_type: member.member_type || 'member',
          });
          setHazards(member.hazard_tags || []);
          setAnimals(member.animal_tags || []);
          setContacts(member.contacts || []);
          setParcelId(member.parcel_id || null);
          setNotes(member.notes || '');

          // Set selected estate for autocomplete
          if (member.estate_id) {
            listEstates({ page: 1, page_size: 100 }).then((data) => {
              const estate = (data.estates || []).find((e) => e.id === member.estate_id);
              if (estate) setSelectedEstate(estate);
            });
          }
        })
        .catch((err) => {
          console.error('Error fetching member:', err);
          setError('Failed to load member data.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  async function handleSubmit(values) {
    setIsSubmitting(true);
    setError(null);

    // The backend requires a name on every contact (ContactWrite.name is
    // min_length=1), and "Add Contact" seeds a blank row. A single unfilled
    // row used to 422 the WHOLE request, silently discarding the hazard and
    // animal edits the user came here to make. Drop rows the user never
    // touched; stop on one that's half-filled so nothing is thrown away.
    const cleanedContacts = contacts
      .map((c) => ({ ...c, name: (c.name || '').trim() }))
      .filter((c) => c.name || (c.phone || '').trim() || (c.email || '').trim());
    if (cleanedContacts.some((c) => !c.name)) {
      setError('Every contact needs a name. Add one, or remove the empty row.');
      setIsSubmitting(false);
      return;
    }

    // POST /fpa-members runs enforce_tenant_ownership, which refuses a null
    // tenant for a SUPER_ADMIN ("no global writes") — and this form never sent
    // one, so creating a member as super admin always 400'd. Everyone else was
    // fine because the backend falls back to their own tenant.
    const resolvedTenantId = tenantId ?? currentUser?.tenant_id ?? null;
    if (!isEditMode && !resolvedTenantId) {
      setError('Select an FPA in the top bar before adding a member.');
      setIsSubmitting(false);
      return;
    }

    try {
      const memberData = {
        ref_number: values.ref_number,
        name: values.name,
        plot_number: values.plot_number || null,
        street: values.street || null,
        area_suburb: values.area_suburb || null,
        size_hectares: values.size_hectares ? parseFloat(values.size_hectares) : null,
        estate_id: selectedEstate ? selectedEstate.id : null,
        member_since: values.member_since || null,
        member_type: values.member_type || 'member',
        hazard_tags: hazards,
        animal_tags: animals,
        contacts: cleanedContacts,
        parcel_id: parcelId,
        notes: notes || null,
      };

      if (isEditMode) {
        await updateMember(id, memberData);
      } else {
        await createMember({ ...memberData, tenant_id: resolvedTenantId });
      }

      navigate('/members?tab=fpa');
    } catch (error) {
      console.error('Error saving member:', error);
      // A 422 `detail` is an ARRAY of objects. Setting it straight onto state
      // and rendering it in the <Alert> threw "Objects are not valid as a
      // React child", so a validation failure showed the user nothing at all.
      setError(parseApiError(error, 'Failed to save member. Please try again later.').message);
      setIsSubmitting(false);
    }
  }

  const handleCancel = () => {
    navigate('/members?tab=fpa');
  };

  const handleLinkParcel = (newParcelId) => {
    setParcelId(newParcelId);
  };

  const handleUnlinkParcel = () => {
    setParcelId(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4, pb: { xs: 10, md: 0 } }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/members?tab=fpa" color="inherit">
            FPA Members
          </Link>
          <Typography color="text.primary">{isEditMode ? 'Edit' : 'Create'}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {isEditMode ? 'Edit FPA Member' : 'Create FPA Member'}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/members?tab=fpa"
          >
            Back to Members
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <form onSubmit={formik.handleSubmit}>
          {/* Section 1: Member Details */}
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Member Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="ref_number"
                  name="ref_number"
                  label="Reference Number"
                  value={formik.values.ref_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.ref_number && Boolean(formik.errors.ref_number)}
                  helperText={formik.touched.ref_number && formik.errors.ref_number}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Member Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="plot_number"
                  name="plot_number"
                  label="Plot Number"
                  value={formik.values.plot_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.plot_number && Boolean(formik.errors.plot_number)}
                  helperText={formik.touched.plot_number && formik.errors.plot_number}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="street"
                  name="street"
                  label="Street"
                  value={formik.values.street}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.street && Boolean(formik.errors.street)}
                  helperText={formik.touched.street && formik.errors.street}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="area_suburb"
                  name="area_suburb"
                  label="Area / Suburb"
                  value={formik.values.area_suburb}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.area_suburb && Boolean(formik.errors.area_suburb)}
                  helperText={formik.touched.area_suburb && formik.errors.area_suburb}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="size_hectares"
                  name="size_hectares"
                  label="Size (hectares)"
                  type="number"
                  value={formik.values.size_hectares}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.size_hectares && Boolean(formik.errors.size_hectares)}
                  helperText={formik.touched.size_hectares && formik.errors.size_hectares}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={estates}
                  getOptionLabel={(option) => option.name}
                  value={selectedEstate}
                  onChange={(event, newValue) => {
                    setSelectedEstate(newValue);
                    formik.setFieldValue('estate_id', newValue ? newValue.id : null);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Estate" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="member_since"
                  name="member_since"
                  label="Member Since"
                  type="date"
                  value={formik.values.member_since}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.member_since && Boolean(formik.errors.member_since)}
                  helperText={formik.touched.member_since && formik.errors.member_since}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="member-type-label">Type</InputLabel>
                  <Select
                    labelId="member-type-label"
                    id="member_type"
                    name="member_type"
                    label="Type"
                    value={formik.values.member_type || 'member'}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="member">FPA Member</MenuItem>
                    <MenuItem value="non_member">Non-member</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 2: Hazards & Animals */}
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Hazards & Animals
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TagSelector
                  label="Hazards"
                  tags={hazards}
                  onChange={setHazards}
                  predefined={PREDEFINED_HAZARDS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TagSelector
                  label="Animals"
                  tags={animals}
                  onChange={setAnimals}
                  predefined={PREDEFINED_ANIMALS}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Section 3: Contacts */}
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Contacts
            </Typography>
            <ContactList contacts={contacts} onChange={setContacts} />
          </Paper>

          {/* Section 4: Parcel Link */}
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Parcel Link
            </Typography>
            <ParcelPicker
              parcelId={parcelId}
              onLink={handleLinkParcel}
              onUnlink={handleUnlinkParcel}
            />
          </Paper>

          {/* Section 5: Notes */}
          <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this member..."
            />
          </Paper>

          {/* Desktop submit buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>

          <MobileFormFooter
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            saveLabel={isEditMode ? 'Save Changes' : 'Create Member'}
          />
        </form>
      </Box>
    </Container>
  );
};

export default FPAMemberForm;
